/**
 * useReportData - Hook for fetching and merging report data
 *
 * Fetches scan results from server and merges with local student names.
 * Server returns only sheet numbers (privacy) - names come from local SQLite.
 *
 * Usage:
 * ```tsx
 * // For worksheet results
 * const { data, loading, error } = useWorksheetResults(worksheetId);
 *
 * // For outcome results
 * const { data, loading, error } = useOutcomeResults(outcomeId);
 *
 * // For summary
 * const { data, loading, error } = useResultsSummary();
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import {
  resultsService,
  isRequestCancelled,
  type ScanResult,
  type OutcomeScan,
  type ResultsSummary,
} from '@/services/results.service';
import { useLocalDbStore } from '@/store/localDbStore';

// =============================================================================
// Types
// =============================================================================

export interface MergedScanResult extends ScanResult {
  studentName: string;
  studentId: string | null;
  isUnknown: boolean;
}

export interface MergedWorksheetResults {
  items: MergedScanResult[];
  total: number;
  worksheetId: string;
  worksheetTitle: string;
}

export interface MergedOutcomeScan extends OutcomeScan {
  studentName: string;
  studentId: string | null;
  isUnknown: boolean;
}

export interface MergedOutcomeResults {
  items: MergedOutcomeScan[];
  total: number;
  outcomeId: string;
  worksheetCount: number;
}

export interface UseReportDataReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// =============================================================================
// Mastery Levels
// =============================================================================

export type MasteryLevel = 'mastered' | 'proficient' | 'developing' | 'needs_support';

export interface MasteryInfo {
  level: MasteryLevel;
  label: string;
  threshold: number;
  color: string;
  icon: string;
}

export const MASTERY_LEVELS: Record<MasteryLevel, MasteryInfo> = {
  mastered: {
    level: 'mastered',
    label: 'Mastered',
    threshold: 85,
    color: 'text-green-600',
    icon: '★',
  },
  proficient: {
    level: 'proficient',
    label: 'Proficient',
    threshold: 70,
    color: 'text-blue-600',
    icon: '✓',
  },
  developing: {
    level: 'developing',
    label: 'Developing',
    threshold: 50,
    color: 'text-yellow-600',
    icon: '⚠',
  },
  needs_support: {
    level: 'needs_support',
    label: 'Needs Support',
    threshold: 0,
    color: 'text-red-600',
    icon: '✗',
  },
};

/**
 * Get mastery level based on percentage score.
 */
export function getMasteryLevel(percentage: number): MasteryInfo {
  if (percentage >= 85) return MASTERY_LEVELS.mastered;
  if (percentage >= 70) return MASTERY_LEVELS.proficient;
  if (percentage >= 50) return MASTERY_LEVELS.developing;
  return MASTERY_LEVELS.needs_support;
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to get worksheet results merged with student names.
 */
export function useWorksheetResults(
  worksheetId: string | null,
  classId: string | null = null,
  limit: number = 100
): UseReportDataReturn<MergedWorksheetResults> {
  const [data, setData] = useState<MergedWorksheetResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const getStudentBySheetNumber = useLocalDbStore((s) => s.getStudentBySheetNumber);
  const isConnected = useLocalDbStore((s) => s.isConnected);

  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!worksheetId) {
      setData(null);
      return;
    }

    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const results = await resultsService.getResultsByWorksheet(
          worksheetId,
          classId,
          limit,
          controller.signal
        );

        // Merge with local student names
        const mergedItems: MergedScanResult[] = results.items.map((item) => {
          const student = isConnected ? getStudentBySheetNumber(item.sheetNumber) : null;
          return {
            ...item,
            studentName: student?.name ?? `Sheet #${item.sheetNumber}`,
            studentId: student?.id ?? null,
            isUnknown: !student,
          };
        });

        if (!controller.signal.aborted) {
          setData({
            ...results,
            items: mergedItems,
          });
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          const message = err instanceof Error ? err.message : 'Failed to load results';
          setError(message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => controller.abort();
  }, [worksheetId, classId, limit, refreshKey, getStudentBySheetNumber, isConnected]);

  return { data, loading, error, refetch };
}

/**
 * Hook to get outcome results merged with student names.
 */
export function useOutcomeResults(
  outcomeId: string | null,
  limit: number = 100
): UseReportDataReturn<MergedOutcomeResults> {
  const [data, setData] = useState<MergedOutcomeResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const getStudentBySheetNumber = useLocalDbStore((s) => s.getStudentBySheetNumber);
  const isConnected = useLocalDbStore((s) => s.isConnected);

  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!outcomeId) {
      setData(null);
      return;
    }

    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const results = await resultsService.getResultsByOutcome(
          outcomeId,
          null, // classId - TODO: add class filtering
          limit,
          controller.signal
        );

        // Merge with local student names
        const mergedItems: MergedOutcomeScan[] = results.items.map((item) => {
          const student = isConnected ? getStudentBySheetNumber(item.sheetNumber) : null;
          return {
            ...item,
            studentName: student?.name ?? `Sheet #${item.sheetNumber}`,
            studentId: student?.id ?? null,
            isUnknown: !student,
          };
        });

        if (!controller.signal.aborted) {
          setData({
            ...results,
            items: mergedItems,
          });
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          const message = err instanceof Error ? err.message : 'Failed to load results';
          setError(message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => controller.abort();
  }, [outcomeId, limit, refreshKey, getStudentBySheetNumber, isConnected]);

  return { data, loading, error, refetch };
}

/**
 * Hook to get results summary.
 */
export function useResultsSummary(): UseReportDataReturn<ResultsSummary> {
  const [data, setData] = useState<ResultsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const results = await resultsService.getResultsSummary(controller.signal);

        if (!controller.signal.aborted) {
          setData(results);
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          const message = err instanceof Error ? err.message : 'Failed to load summary';
          setError(message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => controller.abort();
  }, [refreshKey]);

  return { data, loading, error, refetch };
}

// =============================================================================
// Statistics Helpers
// =============================================================================

/**
 * Calculate statistics from merged results.
 */
export function calculateStats(items: MergedScanResult[]) {
  if (items.length === 0) {
    return {
      count: 0,
      avgPercentage: null,
      masteredCount: 0,
      proficientCount: 0,
      developingCount: 0,
      needsSupportCount: 0,
    };
  }

  const percentages = items.map((i) => i.percentage);
  const avgPercentage = percentages.reduce((a, b) => a + b, 0) / percentages.length;

  return {
    count: items.length,
    avgPercentage: Math.round(avgPercentage * 10) / 10,
    masteredCount: items.filter((i) => i.percentage >= 85).length,
    proficientCount: items.filter((i) => i.percentage >= 70 && i.percentage < 85).length,
    developingCount: items.filter((i) => i.percentage >= 50 && i.percentage < 70).length,
    needsSupportCount: items.filter((i) => i.percentage < 50).length,
  };
}

/**
 * Group results by student (aggregates across worksheets).
 */
export function groupByStudent(items: MergedScanResult[]): Map<string, MergedScanResult[]> {
  const grouped = new Map<string, MergedScanResult[]>();

  for (const item of items) {
    const key = item.studentId ?? item.sheetNumber;
    const existing = grouped.get(key) || [];
    existing.push(item);
    grouped.set(key, existing);
  }

  return grouped;
}
